// Utility functions

function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
}

// Rate limiting implementation
// Rate limiter removed - use the enhanced version from rate-limiter.js instead

// Input validation
const Validator = {
    email(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    },
    
    password(password) {
        return password && password.length >= 8;
    },
    
    name(name) {
        return name && name.trim().length >= 2 && name.trim().length <= 100;
    },
    
    phone(phone) {
        // Optional field - if provided, must be valid format
        if (!phone) return true;
        const re = /^[\d\s\-+()]+$/;
        return re.test(phone) && phone.replace(/\D/g, '').length >= 10;
    },
    
    url(url) {
        // Optional field - if provided, must be valid URL
        if (!url) return true;
        try {
            new URL(url);
            return /^https?:\/\/.+/.test(url);
        } catch {
            return false;
        }
    },
    
    linkedinUrl(url) {
        if (!url) return false; // LinkedIn URL is required
        const re = /^https:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/;
        return re.test(url);
    },
    
    date(dateString) {
        const date = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
    },
    
    time(timeString) {
        const re = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        return re.test(timeString);
    },
    
    textLength(text, minLength, maxLength) {
        if (!text) return minLength === 0;
        const length = text.trim().length;
        return length >= minLength && length <= maxLength;
    },
    
    fileSize(file, maxMB) {
        if (!file) return true;
        return file.size <= maxMB * 1024 * 1024;
    },
    
    fileType(file, allowedTypes) {
        if (!file) return true;
        const fileType = file.type.toLowerCase();
        return allowedTypes.some(type => {
            if (type.endsWith('/*')) {
                // Handle wildcards like 'image/*'
                const category = type.split('/')[0];
                return fileType.startsWith(category + '/');
            }
            return fileType === type;
        });
    },
    
    professionalTitle(title) {
        // Title should be 2-100 chars and not contain special characters
        if (!title) return false;
        const re = /^[a-zA-Z0-9\s\-,&]+$/;
        return this.textLength(title, 2, 100) && re.test(title);
    },
    
    textarea(text, maxLength = 1000) {
        return this.textLength(text, 0, maxLength);
    },
    
    select(value, allowedValues = null) {
        if (!value) return false;
        if (allowedValues) {
            return allowedValues.includes(value);
        }
        return value !== '';
    }
};

// Validation error messages
const ValidationMessages = {
    email: 'Please enter a valid email address',
    password: 'Password must be at least 8 characters',
    name: 'Name must be between 2 and 100 characters',
    phone: 'Please enter a valid phone number (at least 10 digits)',
    url: 'Please enter a valid URL starting with http:// or https://',
    linkedinUrl: 'Please enter a valid LinkedIn profile URL',
    date: 'Please select a future date',
    time: 'Please enter a valid time (HH:MM format)',
    textLength: (min, max) => `Text must be between ${min} and ${max} characters`,
    fileSize: (maxMB) => `File size must not exceed ${maxMB}MB`,
    fileType: (types) => `File must be one of: ${types.join(', ')}`,
    professionalTitle: 'Title must be 2-100 characters and contain only letters, numbers, spaces, hyphens, commas, and ampersands',
    textarea: (max) => `Text must not exceed ${max} characters`,
    select: 'Please select a valid option',
    required: 'This field is required'
};

// Firestore data sanitization
function cleanForFirestore(obj) {
    if (obj === null || obj === undefined) {
        return null;
    }
    
    // Check for File objects (from file inputs)
    if (obj instanceof File) {
        console.warn('File object detected in Firestore data - this should be uploaded separately');
        return null;
    }
    
    // Check for DOM elements
    if (obj instanceof HTMLElement) {
        console.warn('DOM element detected in Firestore data');
        return null;
    }
    
    // Check for other browser objects
    if (typeof window !== 'undefined' && (
        obj instanceof FileList ||
        obj instanceof NodeList ||
        obj instanceof HTMLCollection
    )) {
        console.warn('Browser object detected in Firestore data:', obj.constructor.name);
        return null;
    }
    
    if (obj instanceof Date) {
        return obj;
    }
    
    // Preserve Firebase FieldValue objects (serverTimestamp, arrayUnion, etc.)
    if (obj && obj._methodName) {
        console.log('Preserving FieldValue:', obj._methodName);
        return obj;
    }
    
    // Check for Firebase FieldValue in another way (they might have different structure)
    if (obj && typeof obj === 'object' && obj.constructor && obj.constructor.name === 'FieldValue') {
        console.log('Preserving FieldValue (by constructor)');
        return obj;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => cleanForFirestore(item))
            .filter(item => item !== undefined); // Remove undefined items from arrays
    }
    
    if (typeof obj === 'object') {
        // Check for plain objects only (not class instances)
        const proto = Object.getPrototypeOf(obj);
        if (proto !== Object.prototype && proto !== null) {
            // IMPORTANT: Don't convert Firebase FieldValue objects
            // They need to maintain their special prototype
            if (obj._methodName || (obj.constructor && obj.constructor.name === 'FieldValue')) {
                console.log('Preserving FieldValue object, not converting to plain object');
                return obj;
            }
            
            console.warn('Non-plain object detected:', obj.constructor.name);
            // Try to convert to plain object
            try {
                obj = JSON.parse(JSON.stringify(obj));
            } catch (e) {
                console.error('Could not serialize object:', e);
                return null;
            }
        }
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
            // Skip undefined values completely
            if (value === undefined) {
                continue;
            }
            
            // Skip empty string keys
            if (key === '') {
                console.warn('Empty string key detected and skipped');
                continue;
            }
            
            // Check for invalid key characters
            if (key.match(/[~*\/\[\]]/)) {
                console.warn(`Invalid characters in field name "${key}" - skipping`);
                continue;
            }
            
            // Convert NaN and Infinity to null
            if (typeof value === 'number' && (Number.isNaN(value) || !Number.isFinite(value))) {
                cleaned[key] = null;
            } else if (value === '') {
                // Convert empty strings to null
                cleaned[key] = null;
            } else {
                // Recursively clean nested objects
                const cleanedValue = cleanForFirestore(value);
                if (cleanedValue !== undefined) {
                    cleaned[key] = cleanedValue;
                }
            }
        }
        return cleaned;
    }
    
    // Handle primitive values
    if (typeof obj === 'number' && (Number.isNaN(obj) || !Number.isFinite(obj))) {
        return null;
    }
    
    if (obj === '') {
        return null;
    }
    
    // Check for functions
    if (typeof obj === 'function') {
        console.warn('Function detected in Firestore data');
        return null;
    }
    
    return obj;
}

// Error handling
function handleError(error, userMessage = 'An error occurred. Please try again.') {
    console.error('Error:', error);
    
    // Log to analytics if available
    if (typeof firebase !== 'undefined' && firebase.analytics) {
        firebase.analytics().logEvent('error', {
            error_message: error.message,
            error_stack: error.stack
        });
    }
    
    // Show user-friendly message using toast
    if (window.toast) {
        window.toast.error(userMessage);
    } else {
        alert(userMessage);
    }
}

// Secure content sanitization wrapper
async function sanitizeContent(content) {
    // Try to use DOMPurify if available
    if (typeof DOMPurify !== 'undefined') {
        return DOMPurify.sanitize(content);
    }
    
    // Try to lazy load DOMPurify
    try {
        const { getDOMPurify } = await import('./lazy-loader.js');
        const purify = await getDOMPurify();
        if (purify) {
            return purify.sanitize(content);
        }
    } catch (error) {
        console.warn('Failed to load DOMPurify:', error);
    }
    
    // Fallback basic sanitization - return plain text only
    const div = document.createElement('div');
    div.textContent = content;
    return div.textContent;
}

// Synchronous version for immediate use
function sanitizeContentSync(content) {
    if (typeof DOMPurify !== 'undefined') {
        return DOMPurify.sanitize(content);
    }
    // Fallback basic sanitization - return plain text only
    const div = document.createElement('div');
    div.textContent = content;
    return div.textContent;
}

// Toast Notification System
class Toast {
    constructor() {
        this.container = null;
        this.init();
    }
    
    init() {
        // Create container if it doesn't exist
        if (!document.getElementById('toast-container')) {
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            this.container.className = 'toast-container';
            document.body.appendChild(this.container);
        } else {
            this.container = document.getElementById('toast-container');
        }
    }
    
    show(message, type = 'info', duration = 5000) {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        
        // Icons for different types
        const icons = {
            success: '✓',
            error: '✕',
            warning: '⚠',
            info: 'ℹ'
        };
        
        toast.innerHTML = `
            <span class="toast-icon">${icons[type] || icons.info}</span>
            <span class="toast-message">${sanitizeContentSync(message)}</span>
            <button class="toast-close" aria-label="Dismiss notification">×</button>
        `;
        
        // Add ARIA attributes for accessibility
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        
        // Add click handler to close button
        const closeBtn = toast.querySelector('.toast-close');
        closeBtn.addEventListener('click', () => {
            toast.classList.remove('toast-show');
            setTimeout(() => toast.remove(), 300);
        });
        
        // Add to container
        this.container.appendChild(toast);
        
        // Trigger animation
        setTimeout(() => toast.classList.add('toast-show'), 10);
        
        // Auto dismiss
        if (duration > 0) {
            setTimeout(() => {
                toast.classList.remove('toast-show');
                setTimeout(() => toast.remove(), 300);
            }, duration);
        }
        
        return toast;
    }
    
    success(message, duration) {
        return this.show(message, 'success', duration);
    }
    
    error(message, duration) {
        return this.show(message, 'error', duration);
    }
    
    warning(message, duration) {
        return this.show(message, 'warning', duration);
    }
    
    info(message, duration) {
        return this.show(message, 'info', duration);
    }
}

// Create singleton instance
const toast = new Toast();

// Make toast globally available
window.toast = toast;

// Form validation utilities
const FormValidation = {
    // Initialize validation for a form
    init(formId) {
        const form = document.getElementById(formId);
        if (!form) return;
        
        // Add validation to all inputs
        const inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            // Skip submit buttons
            if (input.type === 'submit' || input.type === 'button') return;
            
            // Add validation icon if not exists
            const formGroup = input.closest('.form-group');
            if (formGroup && !formGroup.querySelector('.validation-icon')) {
                const icon = document.createElement('span');
                icon.className = 'validation-icon';
                icon.setAttribute('aria-hidden', 'true');
                formGroup.appendChild(icon);
                
                const message = document.createElement('div');
                message.className = 'validation-message';
                // Create unique ID for error message
                const messageId = `${formId}-${input.id || input.name || 'field'}-error`;
                message.id = messageId;
                message.setAttribute('role', 'alert');
                message.setAttribute('aria-live', 'polite');
                formGroup.appendChild(message);
                
                // Link input to error message
                input.setAttribute('aria-describedby', messageId);
            }
            
            // Add character counter for textareas
            if (input.tagName === 'TEXTAREA' && input.maxLength > 0) {
                const counter = document.createElement('span');
                counter.className = 'character-counter';
                formGroup.appendChild(counter);
                this.updateCharacterCount(input, counter);
                
                input.addEventListener('input', () => {
                    this.updateCharacterCount(input, counter);
                });
            }
            
            // Add event listeners
            input.addEventListener('blur', () => this.validateField(input));
            input.addEventListener('input', () => {
                // Clear error state on input
                const formGroup = input.closest('.form-group');
                if (formGroup && formGroup.classList.contains('has-error')) {
                    this.clearValidation(input);
                }
            });
        });
    },
    
    // Validate a single field
    validateField(input) {
        const formGroup = input.closest('.form-group');
        if (!formGroup) return true;
        
        const icon = formGroup.querySelector('.validation-icon');
        const message = formGroup.querySelector('.validation-message');
        
        let isValid = true;
        let errorMessage = '';
        
        // Check required fields
        if (input.hasAttribute('required') && !input.value.trim()) {
            isValid = false;
            errorMessage = ValidationMessages.required;
        } else {
            // Validate based on input type
            switch (input.type) {
            case 'email':
                if (!Validator.email(input.value)) {
                    isValid = false;
                    errorMessage = ValidationMessages.email;
                }
                break;
            case 'password':
                if (!Validator.password(input.value)) {
                    isValid = false;
                    errorMessage = ValidationMessages.password;
                }
                break;
            case 'tel':
                if (input.value && !Validator.phone(input.value)) {
                    isValid = false;
                    errorMessage = ValidationMessages.phone;
                }
                break;
            case 'url':
                if (input.value && !Validator.url(input.value)) {
                    isValid = false;
                    errorMessage = ValidationMessages.url;
                }
                break;
            }
            
            // Check data attributes for custom validation
            if (input.dataset.validationType) {
                switch (input.dataset.validationType) {
                case 'name':
                    if (!Validator.name(input.value)) {
                        isValid = false;
                        errorMessage = ValidationMessages.name;
                    }
                    break;
                case 'linkedinUrl':
                    if (!Validator.linkedinUrl(input.value)) {
                        isValid = false;
                        errorMessage = ValidationMessages.linkedinUrl;
                    }
                    break;
                }
            }
        }
        
        // Update UI
        if (isValid) {
            formGroup.classList.remove('has-error');
            formGroup.classList.add('has-success');
            if (icon) icon.textContent = '✓';
            if (message) message.textContent = '';
            input.setAttribute('aria-invalid', 'false');
        } else {
            formGroup.classList.remove('has-success');
            formGroup.classList.add('has-error');
            if (icon) icon.textContent = '✕';
            if (message) message.textContent = errorMessage;
            input.setAttribute('aria-invalid', 'true');
        }
        
        return isValid;
    },
    
    // Clear validation state
    clearValidation(input) {
        const formGroup = input.closest('.form-group');
        if (formGroup) {
            formGroup.classList.remove('has-error', 'has-success');
            const icon = formGroup.querySelector('.validation-icon');
            const message = formGroup.querySelector('.validation-message');
            if (icon) icon.textContent = '';
            if (message) message.textContent = '';
            input.removeAttribute('aria-invalid');
        }
    },
    
    // Update character count
    updateCharacterCount(textarea, counter) {
        const current = textarea.value.length;
        const max = textarea.maxLength;
        counter.textContent = `${current}/${max}`;
        
        if (current > max * 0.9) {
            counter.classList.add('error');
            counter.classList.remove('warning');
        } else if (current > max * 0.7) {
            counter.classList.add('warning');
            counter.classList.remove('error');
        } else {
            counter.classList.remove('warning', 'error');
        }
    },
    
    // Validate entire form
    validateForm(formId) {
        const form = document.getElementById(formId);
        if (!form) return false;
        
        const inputs = form.querySelectorAll('input, select, textarea');
        let isValid = true;
        const errors = [];
        
        inputs.forEach(input => {
            if (input.type !== 'submit' && input.type !== 'button') {
                if (!this.validateField(input)) {
                    isValid = false;
                    // Collect error messages for announcement
                    const formGroup = input.closest('.form-group');
                    const message = formGroup?.querySelector('.validation-message');
                    if (message && message.textContent) {
                        const label = formGroup.querySelector('label');
                        const fieldName = label ? label.textContent : 'Field';
                        errors.push(`${fieldName}: ${message.textContent}`);
                    }
                }
            }
        });
        
        // Announce errors if any (requires aria-live.js to be imported)
        if (!isValid && errors.length > 0 && window.ariaLive) {
            window.ariaLive.announceFormErrors(errors);
        }
        
        return isValid;
    }
};

// File upload progress
const FileUploadProgress = {
    show(inputElement, progress = 0) {
        const formGroup = inputElement.closest('.form-group');
        if (!formGroup) return;
        
        let progressBar = formGroup.querySelector('.upload-progress');
        if (!progressBar) {
            progressBar = document.createElement('div');
            progressBar.className = 'upload-progress';
            formGroup.style.position = 'relative';
            formGroup.appendChild(progressBar);
        }
        
        progressBar.style.width = `${progress}%`;
        
        if (progress >= 100) {
            setTimeout(() => {
                progressBar.style.opacity = '0';
                setTimeout(() => progressBar.remove(), 300);
            }, 1000);
        }
    }
};

// Loading utilities
const Loading = {
    // Add loading state to button
    button(button, loading = true) {
        if (loading) {
            button.classList.add('btn-loading');
            button.disabled = true;
            button.dataset.originalText = button.textContent;
        } else {
            button.classList.remove('btn-loading');
            button.disabled = false;
            if (button.dataset.originalText) {
                button.textContent = button.dataset.originalText;
            }
        }
    },
    
    // Show/hide loading overlay
    overlay(show = true, message = 'Loading...') {
        let overlay = document.getElementById('loading-overlay');
        
        if (show) {
            if (!overlay) {
                overlay = document.createElement('div');
                overlay.id = 'loading-overlay';
                overlay.className = 'loading-overlay';
                overlay.innerHTML = `
                    <div class="loading-card">
                        <div class="spinner spinner-primary"></div>
                        <p>${sanitizeContentSync(message)}</p>
                    </div>
                `;
                document.body.appendChild(overlay);
            }
            overlay.style.display = 'flex';
        } else if (overlay) {
            overlay.style.display = 'none';
        }
    },
    
    // Generate skeleton card HTML
    skeletonCard() {
        return `
            <div class="skeleton-card">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-text skeleton-title"></div>
                <div class="skeleton-text skeleton-subtitle"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-button"></div>
            </div>
        `;
    },
    
    // Generate multiple skeleton cards
    skeletonGrid(count = 6) {
        return Array(count).fill(this.skeletonCard()).join('');
    }
};

// Validate and sanitize URLs to prevent javascript: and other malicious schemes
function safeHref(url) {
    if (!url) return '#';
    
    // Trim and check if it's a string
    const trimmedUrl = String(url).trim();
    
    // Only allow http/https URLs
    try {
        const parsedUrl = new URL(trimmedUrl);
        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
            return trimmedUrl;
        }
    } catch {
        // Invalid URL format
    }
    
    // Return safe fallback for invalid URLs
    return '#invalid-link';
}

// Debounce function to limit function calls
function debounce(func, delay) {
    let timeoutId;
    return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

export { 
    formatDate, 
    Validator, 
    ValidationMessages,
    handleError, 
    sanitizeContent,
    sanitizeContentSync,
    toast,
    FormValidation,
    FileUploadProgress,
    Loading,
    safeHref,
    debounce,
    cleanForFirestore 
};